<template>
  <AppNavbar />
  <div class="vx-admin">
    <div style="max-width: 1040px">

      <div class="mb-3">
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span><span>Estado de cuenta</span>
        </div>
        <h1 class="vx-titulo">Estado de cuenta</h1>
        <div class="vx-ayuda mt-1">
          Cuánto debe una persona, sumando todas las {{ vocabulario.plural.toLowerCase() }} donde es responsable de pago.
        </div>
      </div>

      <!-- Buscador. Se busca PERSONA, no suscripción: un responsable puede
           tener varios hijos y lo que importa acá es el total. -->
      <div class="vx-panel mb-3">
        <div class="p-3">
          <div class="position-relative" style="max-width: 460px">
            <input
              id="buscador-responsable"
              v-model.trim="busqueda"
              class="vx-campo"
              placeholder="Buscar responsable por nombre o documento…"
              @input="buscar"
            >
            <ul
              v-if="resultados.length"
              class="list-unstyled position-absolute w-100 mb-0 vx-panel"
              data-test="resultados-responsable"
              style="z-index: 20; box-shadow: var(--vx-sombra); max-height: 260px; overflow-y: auto"
            >
              <li
                v-for="c in resultados"
                :key="c.cliente_id"
                class="px-3 py-2 border-bottom"
                style="cursor: pointer"
                @click="elegir(c)"
              >
                <div style="font-size: 13px; font-weight: 550">{{ c.nombre_completo }}</div>
                <div class="vx-celda-sub vx-num">{{ documentoDe(c) || 'Sin documento' }}</div>
              </li>
            </ul>
          </div>
          <!-- Un buscador que devuelve la misma pantalla vacia cuando falla y
               cuando no hay coincidencias es un buscador que no se puede
               diagnosticar: el operador no sabe si escribio mal el nombre o si
               el sistema no le esta contestando. -->
          <div v-if="errorBusqueda" class="vx-error mt-2" data-test="error-busqueda">
            {{ errorBusqueda }}
          </div>
          <div v-else-if="buscando" class="vx-ayuda mt-2">Buscando…</div>
          <div v-else-if="sinCoincidencias" class="vx-ayuda mt-2" data-test="busqueda-sin-resultados">
            Ninguna persona coincide con «{{ busqueda }}».
          </div>
          <div v-else class="vx-ayuda mt-2">Escribí al menos dos caracteres.</div>
        </div>
      </div>

      <div v-if="cargando" class="vx-panel vx-vacio">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span class="vx-vacio-texto">Cargando…</span>
      </div>

      <div v-else-if="error" class="vx-panel vx-vacio">
        <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">No se pudo cargar el estado de cuenta</span>
        <span class="vx-vacio-texto">{{ error }}</span>
      </div>

      <template v-else-if="estado">
        <!-- Cabecera de la persona -->
        <div class="d-flex align-items-center gap-3 mb-3">
          <div class="vx-iniciales" style="width: 44px; height: 44px; font-size: 15px">
            {{ iniciales(estado.cliente.nombre_completo) }}
          </div>
          <div>
            <div style="font-size: 16px; font-weight: 620" data-test="cliente-nombre">
              {{ estado.cliente.nombre_completo }}
            </div>
            <div class="vx-ayuda">{{ documentoDe(estado.cliente) || 'Sin documento' }}</div>
          </div>
        </div>

        <!-- Cifras. Vencido primero: es lo que el operador necesita ver de
             entrada, no una suma neutra. -->
        <div class="row g-2 mb-3">
          <div v-for="c in cifras" :key="c.etiqueta" class="col-6 col-lg-3">
            <div class="vx-panel px-3 py-2">
              <div class="vx-ayuda">{{ c.etiqueta }}</div>
              <div class="vx-num" :style="{ fontSize: '22px', fontWeight: 650, letterSpacing: '-0.02em', color: c.color }">
                {{ c.valor }}
              </div>
            </div>
          </div>
        </div>

        <!-- Sin nada que deber, con todas las letras: un padre al día no es
             un error ni una pantalla vacía sospechosa. -->
        <div v-if="!estado.suscripciones.length" class="vx-panel vx-vacio" data-test="sin-deuda">
          <i class="bi bi-check2-circle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
          <span class="vx-vacio-titulo">No tiene nada pendiente</span>
          <span class="vx-vacio-texto">
            No hay {{ vocabulario.plural.toLowerCase() }} con saldo a nombre de esta persona.
          </span>
        </div>

        <!-- Un panel por suscripción: el Criterio 1 pide ver las tres deudas
             de tres hijos juntas, no una lista plana donde hay que adivinar
             de quién es cada línea. -->
        <div
          v-for="grupo in estado.suscripciones" :key="grupo.cr_suscripcion_id"
          class="vx-panel mb-3" data-test="grupo-suscripcion"
        >
          <div class="vx-panel-cabecera">
            <div>
              <router-link
                class="vx-panel-titulo" style="text-decoration: none"
                :to="{ name: 'SuscripcionDetalle', params: { id: grupo.cr_suscripcion_id } }"
              >
                {{ grupo.nombre }}
              </router-link>
              <div class="vx-ayuda mt-1">
                {{ grupo.esquema_nombre || '—' }}
                <span v-if="grupo.suscripcion_estado !== 'ACTIVA'" class="vx-estado vx-estado-neutro ms-1">
                  {{ grupo.suscripcion_estado === 'FINALIZADA' ? 'Finalizada' : 'Suspendida' }}
                </span>
              </div>
            </div>
            <div class="text-end">
              <div class="vx-num" style="font-size: 16px; font-weight: 650">{{ formatearMonto(grupo.saldo) }}</div>
              <div v-if="Number(grupo.vencido)" class="vx-ayuda" style="color: #a5281a">
                {{ formatearMonto(grupo.vencido) }} vencido
              </div>
            </div>
          </div>

          <table class="vx-tabla">
            <thead>
              <tr>
                <th>Período</th>
                <th>Concepto</th>
                <th>Vence</th>
                <th class="vx-col-num">Total</th>
                <th class="vx-col-num">Saldo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in grupo.deudas" :key="d.cr_deuda_id">
                <td class="vx-num">{{ d.periodo }}</td>
                <td>{{ d.descripcion }}</td>
                <td class="vx-num">
                  {{ fechaCorta(d.fecha_vencimiento) }}
                  <span v-if="d.vencida" class="vx-estado vx-estado-atencion ms-1">Vencida</span>
                </td>
                <td class="vx-col-num vx-num">{{ formatearMonto(d.monto_total) }}</td>
                <td class="vx-col-num vx-num" style="font-weight: 600">{{ formatearMonto(d.saldo) }}</td>
                <td>
                  <span class="vx-estado" :class="estadoDeuda(d).clase">{{ estadoDeuda(d).texto }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Sin nada buscado todavía. -->
      <div v-else class="vx-panel vx-vacio">
        <i class="bi bi-person-lines-fill" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">Buscá una persona para ver su estado de cuenta</span>
      </div>
    </div>
  </div>
</template>

<script setup>
// Estado de cuenta por responsable — CR-E5-002.
// SPEC CF12 · Criterio de aceptación 1.
//
// "Dos personas asociadas a tres suscripciones producen UN estado de cuenta
// por responsable, con las tres deudas": es la pregunta del mostrador,
// "¿cuánto debo, sumando todos mis hijos?", respondida de un vistazo.

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import {
  obtenerEstadoCuenta, buscarClientes,
  obtenerVocabulario, VOCABULARIO_POR_DEFECTO,
  formatearMonto, fechaCorta, iniciales, documentoDe, estadoDeuda
} from '@/services/cobranzaService'

const route = useRoute()
const router = useRouter()

const busqueda = ref('')
const resultados = ref([])
const estado = ref(null)
const cargando = ref(false)
const error = ref('')
const buscando = ref(false)
const errorBusqueda = ref('')
// El termino de la ULTIMA busqueda que efectivamente respondio. Sin esto,
// «ninguna coincidencia» se dibujaria mientras todavia se esta tipeando.
const buscado = ref('')
const sinCoincidencias = computed(() =>
  Boolean(buscado.value) && buscado.value === busqueda.value && !resultados.value.length
)

const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['deuda.singular'],
  plural: etiquetas.value['deuda.plural']
}))

const cifras = computed(() => {
  if (!estado.value) return []
  const r = estado.value.resumen
  return [
    { etiqueta: `${vocabulario.value.plural} vencidas`, valor: formatearMonto(r.vencido_total), color: Number(r.vencido_total) ? '#a5281a' : undefined },
    { etiqueta: 'Saldo total', valor: formatearMonto(r.saldo_total) },
    { etiqueta: 'Suscripciones', valor: r.suscripciones },
    { etiqueta: vocabulario.value.plural, valor: r.deudas }
  ]
})

let temporizador = null
function buscar () {
  clearTimeout(temporizador)
  errorBusqueda.value = ''
  buscado.value = ''
  if (busqueda.value.length < 2) { resultados.value = []; buscando.value = false; return }
  buscando.value = true
  temporizador = setTimeout(async () => {
    const termino = busqueda.value
    try {
      resultados.value = await buscarClientes(termino)
      buscado.value = termino
    } catch (err) {
      resultados.value = []
      // El listado de clientes es un endpoint aparte del estado de cuenta y
      // tiene su propio permiso (`ver_cliente`): puede fallar solo, y hasta
      // ahora lo hacia sin dejar rastro en la pantalla.
      errorBusqueda.value = err?.userMessage || 'No se pudo buscar a la persona.'
    } finally {
      buscando.value = false
    }
  }, 250)
}

function elegir (cliente) {
  resultados.value = []
  busqueda.value = ''
  buscado.value = ''
  errorBusqueda.value = ''
  router.push({ name: 'CobranzaEstadoCuenta', params: { clienteId: cliente.cliente_id } })
}

async function cargar (clienteId) {
  if (!clienteId) { estado.value = null; return }
  cargando.value = true
  error.value = ''
  try {
    estado.value = await obtenerEstadoCuenta(clienteId)
  } catch (err) {
    error.value = err?.userMessage || 'Error inesperado al consultar el estado de cuenta.'
    estado.value = null
  } finally {
    cargando.value = false
  }
}

onMounted(async () => {
  etiquetas.value = await obtenerVocabulario()
  await cargar(route.params.clienteId)
})

// Elegir a otra persona desde el buscador cambia el parámetro de ruta sin
// desmontar la vista: sin este watch, la segunda búsqueda quedaría mostrando
// el estado de cuenta de la primera.
watch(() => route.params.clienteId, (nuevo) => { cargar(nuevo) })
</script>
